'use client';

import { useMemo, useState } from 'react';

import { useGetTasksQuery } from '@/features/tasks/api/tasksApi';
import { formatDueDateLabel, getTimelineStatusLabel } from '@/utils/tasks';
import { Badge, Meter, SmartInput } from '@web/ui';
import { formatHours } from '@web/utils';

import styles from './TimeEntryTaskSelect.module.scss';

import type { Task } from '@/features/tasks/types';

type TimeEntryTaskSelectProps = {
  assigneeId?: string;
  onTaskSelect: (task: Task) => void;
};

function getPrimaryAssignee(task: Task) {
  return task.assignees?.[0] ?? null;
}

function getContextLabel(task: Task) {
  if (task.job?.name) {
    return `${task.job.name}${task.deliverable?.name ? ` > ${task.deliverable.name}` : ''}`;
  }

  return task.client?.company?.name ?? 'Internal';
}

export function TimeEntryTaskSelect({
  assigneeId,
  onTaskSelect,
}: TimeEntryTaskSelectProps) {
  const [search, setSearch] = useState('');

  const { data, isLoading, isFetching } = useGetTasksQuery({
    page: 1,
    page_size: 20,
    task_type: 'all',
    for_time_logging: true,
    query: search || undefined,
    assignee_id: assigneeId || undefined,
    is_descending: true,
    sort_field: 'created_at',
  });

  const tasks = useMemo(() => data?.data?.results ?? [], [data]);
  const isBusy = isLoading || isFetching;

  return (
    <div className={styles.timeEntryTaskSelect}>
      <SmartInput
        type="text"
        name="time-entry-task-search"
        value={search}
        label="Search tasks..."
        onChange={setSearch}
      />

      <div className={styles.timeEntryTaskSelect__list}>
        {tasks.length === 0 && !isBusy ? (
          <div className={styles.timeEntryTaskSelect__empty}>
            No tasks found.
          </div>
        ) : null}

        {isBusy ? (
          <div className={styles.timeEntryTaskSelect__empty}>
            Loading tasks...
          </div>
        ) : null}

        {tasks.map((task) => {
          const assignee = getPrimaryAssignee(task);

          return (
            <button
              key={task.id}
              type="button"
              className={styles.timeEntryTaskSelect__option}
              onClick={() => onTaskSelect(task)}
            >
              <span>
                <span className={styles.timeEntryTaskSelect__taskName}>
                  <span className={styles.timeEntryTaskSelect__title}>
                    {task.title || 'Untitled task'}
                  </span>
                  <Badge label={task.status} tone="default" size="sm" />
                </span>
                <span className={styles.timeEntryTaskSelect__meta}>
                  {getContextLabel(task)}
                </span>
              </span>

              <span className={styles.timeEntryTaskSelect__meters}>
                <Meter
                  percentage={assignee?.actual_time_tracked_percentage ?? 0}
                  label={`LOE ${formatHours(assignee?.tracked_hours)} / ${formatHours(assignee?.level_of_effort)}h`}
                  ariaLabel="Task LOE meter"
                />
                <Meter
                  percentage={task.due_date_time_percentage ?? 0}
                  label={`${formatDueDateLabel(task.due_date)} - ${getTimelineStatusLabel(task.due_date, task.days_to_due)}`}
                  ariaLabel="Task timeline meter"
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
